import {useCallback, useRef} from 'react'
import {updateNode} from '../../editorActions'
import {ImagePersistenceService} from '../../integrations/design-library/ImagePersistenceService'

/**
 * Hook that handles the "apply removed-background" flow:
 * 1. Creates a preview blob URL and updates the node immediately.
 * 2. Persists the image via ImagePersistenceService in the background.
 * 3. Replaces the preview URL with the persisted link and revokes the blob URL.
 *
 * Uses a generation counter so that if user applies multiple times, only the
 * latest apply's upload result is written back — earlier ones are discarded.
 *
 * @param {{ runtime: object, node: object | null }} params
 * @returns {{ handleApply: (blob: Blob) => void }}
 */
export function useRemoveBgApply({runtime, node}) {
    const genRef = useRef(0)

    const handleApply = useCallback(
        (blob) => {
            if (!blob || !node?.id || !runtime?.dispatch) return

            const nodeId = node.id
            const gen = ++genRef.current

            const blobUrl = URL.createObjectURL(blob)
            runtime.dispatch(updateNode(nodeId, {content: {src: blobUrl}}))

            const file = new File([blob], 'image.png', {type: 'image/png'})
            ImagePersistenceService.persistImage(file).then((result) => {
                if (gen !== genRef.current) {
                    URL.revokeObjectURL(blobUrl)
                    return
                }

                if (result?.link && runtime?.dispatch) {
                    runtime.dispatch(updateNode(nodeId, {content: {src: result.link}}))
                    URL.revokeObjectURL(blobUrl)
                }
            })
        },
        [runtime, node],
    )

    return {handleApply}
}
